import { useState, useEffect, ReactElement } from 'react';
import { Pivot, PivotItem } from '@fluentui/react';
import DocumentTitle from 'react-document-title';
import { Spinner } from '@fluentui/react';
import SampleProfile from '@/services/SampleProfile';
import BackButton from '@/components/buttons/BackButton';
import { getCdnUrl } from '@/utils/Cdn';
import { ICategory, ISampleProfile } from '@/services/SampleProfile';
import { SampleProfilesContainer } from './NewSampleProfileSavingsPlanStyle';
import SampleProfileCard from '@/components/SampleProfile/SampleProfileCard';

interface INewSampleProfileSavingsPlanProps {
    publicVersion: boolean;
}

export interface TabType {
    id: string;
    name: string;
    icon?: ReactElement;
}

const NewSampleProfileSavingsPlan = (props: INewSampleProfileSavingsPlanProps) => {
    const [activeTab, setActiveTab] = useState<string>('all');
    const [originalSampleProfiles, setOriginalSampleProfiles] = useState<ISampleProfile[]>([]);
    const [allSampleProfiles, setAllSampleProfiles] = useState<ISampleProfile[]>([]);
    const [tabs, setTabs] = useState<TabType[]>([{ id: 'all', name: 'Todas' }]);
    const [mainLoading, setMainLoading] = useState<boolean>(true);
    const [cardsLoading, setCardsLoading] = useState<boolean>(false);

    const sampleAllTabDescription =
        'Aquí tienes una visión de todas las carteras ejemplo con sus distintas características, dependiendo si lo que buscas es invertir sin coste. Promocionas, confías en una gestora o prefieres hacerlo en otros clientes, además de las carteras con objetivos definidos, temáticas, y las que solo seleccionan un fondo que por su naturaleza ya tiene un equilibrio suficiente, One Shot.';

    /*****************************************************************
     * Obtiene las categorías (tabs) al inicio *
     *****************************************************************/
    useEffect(() => {
        setMainLoading(true);
        SampleProfile.getTabs()
            .then((categories: ICategory[]) => {
                if (categories && categories.length > 0) {
                    setTabs((prevTabs) => {
                        const newTabs = categories.map((category) => ({
                            id: category.id.toString(),
                            name: category.name,
                        }));
                        const updatedTabs = [...prevTabs, ...newTabs].filter(
                            (tab, index, self) =>
                                index === self.findIndex((t) => t.id === tab.id)
                        );

                        return updatedTabs;
                    });
                }
            })
            .catch((error) => {
                console.error('Error al recoger las tabs >>>', error);
            })
            .finally(() => {
                setCardsLoading(false);
                setMainLoading(false);
            });
    }, []);

    /*****************************************************************
     * Obtiene todos los perfiles de ejemplo al inicio
     *****************************************************************/
    useEffect(() => {
        setCardsLoading(true);
        SampleProfile.getAllSampleProfiles()
            .then((sampleProfileResponse: ISampleProfile[]) => { 
                setOriginalSampleProfiles(sampleProfileResponse);
                setAllSampleProfiles(sampleProfileResponse);
            })
            .catch((error) => {
                setOriginalSampleProfiles([]);
                setAllSampleProfiles([]);
                console.error('Error en useEffect', error);
            })
            .finally(() => {
                setCardsLoading(false);
            });
    }, []);
    

    /*****************************************************************
     * Obtiene perfiles de ejemplo por categoría al cambiar de pestaña
     *****************************************************************/
    useEffect(() => {
        setCardsLoading(true);
        if (activeTab === 'all') {
          // Mostrar todos los perfiles
            setAllSampleProfiles(originalSampleProfiles);
            setCardsLoading(false);
        } else {
            const categoryId = parseInt(activeTab);
            SampleProfile.getSampleProfilesByCategory(categoryId - 1).then((category: ICategory) => {
                const filtered = originalSampleProfiles.filter(
                    (profile) => profile.type === (categoryId - 1)
                );
                setAllSampleProfiles(filtered);
                setCardsLoading(false);
            }).catch((error) => {
                console.error('Error al obtener perfiles por categoría:', error);
                setCardsLoading(false);
            });
        }
    }, [activeTab]);



    return (
        <DocumentTitle title="IronIA - Carteras Ejemplo">
            <div>
                {!props.publicVersion && <BackButton />}
                <div className="ms-Grid-row savings-plans section-header">
                    <div className="ms-Grid-col ms-md3">
                        <div className="image-experience-container">
                            <img src={getCdnUrl('SampleProfiles.webp')} alt="Carteras Ejemplo" />
                        </div>
                    </div>
                    <div className="ms-Grid-col ms-md9">
                        <h2 className="highlighted">Carteras Ejemplo</h2>
                        <p className="description">{sampleAllTabDescription}</p>
                    </div>
                </div>

                <div style={{margin: "48px"}}></div>

                {/* Tabs */}
                <div className='ms-Grid-row'>
                    <Pivot onLinkClick={(item) => setActiveTab(item?.props.itemKey || 'all')} selectedKey={activeTab}>
                        {tabs.map((tab) => (
                            <PivotItem headerText={tab.name} itemKey={tab.id} key={tab.id}>
                                <div>
                                    {mainLoading ? (
                                        <Spinner label="Cargando..." />
                                    ) : allSampleProfiles.length > 0 ? (
                                        <SampleProfilesContainer>
                                            {allSampleProfiles.map((sampleProfile) => (
                                                <SampleProfileCard
                                                    key={sampleProfile.id}
                                                    sampleProfileId={sampleProfile?.id!}
                                                    imgHeaderSource={sampleProfile?.image!}
                                                    cardTitle={sampleProfile.name}
                                                    cardPrice={sampleProfile?.subscription!.price?.toString()!}
                                                    cardBody={sampleProfile?.description!}
                                                    cardJsonFundsData={sampleProfile?.jsonFundsData!}
                                                />
                                            ))}
                                        </SampleProfilesContainer>
                                    ) : (
                                        <span>No hay perfiles para esta categoría.</span>
                                    )}
                                </div>
                            </PivotItem>
                        ))}
                    </Pivot>
                </div>
            </div>
        </DocumentTitle>
    );
};

export default NewSampleProfileSavingsPlan;
